import { Card, Tag } from 'antd'
import {
  type CoverageCalculation,
  getEffectiveZoneArea,
  getTypeBLimits,
  InspectionStrategy,
} from 'features/libra/stepComponents/zoneAnalysis/niiStrategy'
import { type ZoneParams } from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import { FC } from 'react'
import styled from 'styled-components'

const ResultCard = styled(Card)`
  border: 2px solid ${({ theme }) => theme.forecast.border};

  .ant-card-head {
    background: ${({ theme }) => theme.forecast.background2};
    border-bottom: 1px solid ${({ theme }) => theme.forecast.border};
  }
`

const ResultValue = styled.div`
  font-size: 48px;
  font-weight: 700;
  color: ${({ theme }) => theme.forecast.primary};
  text-align: center;
  margin: 16px 0;
`

const ResultSubtext = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.forecast.secondaryText};
  text-align: center;
  margin-bottom: 16px;
`

const FormulaDisplay = styled.div`
  background: ${({ theme }) => theme.forecast.background3};
  padding: 16px 20px;
  border-radius: 6px;
  font-family: 'Monaco', 'Courier New', monospace;
  font-size: 18px;
  line-height: 2;
  margin: 16px 0;
  color: ${({ theme }) => theme.forecast.defaultText};

  sub {
    font-size: 80%;
  }
`

const FormulaLine = styled.div`
  margin: 8px 0;
`

const ClampedNote = styled.span`
  color: ${({ theme }) => theme.forecast.warning};
  font-size: 14px;
  font-style: italic;
`

const ModifierTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.forecast.border};
`

function getModifierColor(modifier?: number) {
  if (!modifier) return 'default'
  return modifier > 1 ? 'red' : modifier < 1 ? 'green' : 'gold'
}

export interface CoverageCalculationBreakdownProps {
  zone: ZoneParams
  coverage: CoverageCalculation
  strategyType: InspectionStrategy
  confidence: 'low' | 'med' | 'high'
}

export const CoverageCalculationBreakdown: FC<
  CoverageCalculationBreakdownProps
> = ({ zone, coverage, strategyType, confidence }) => {
  const zoneArea = getEffectiveZoneArea(zone)

  if (strategyType === InspectionStrategy.C) {
    return (
      <ResultCard title='Required Coverage - Type C'>
        <ResultValue>100%</ResultValue>
        <ResultSubtext>
          {zoneArea.toFixed(2)} m² total zone area — Type C always requires full
          coverage
        </ResultSubtext>
      </ResultCard>
    )
  }

  const raw = coverage.raw_calculated_percent
  const wasClamped =
    raw !== undefined &&
    coverage.required_coverage_percent !== undefined &&
    raw !== coverage.required_coverage_percent

  const typeBLimits =
    strategyType === InspectionStrategy.B
      ? getTypeBLimits(confidence)
      : undefined

  const clampedSubtext = !wasClamped
    ? ''
    : strategyType === InspectionStrategy.A
      ? ' (clamped to minimum 2.5%)'
      : typeBLimits
        ? ` (clamped to ${raw! < typeBLimits.min ? `floor ${typeBLimits.min}%` : `ceiling ${typeBLimits.max}%`})`
        : ''

  return (
    <ResultCard title={`Required Coverage - Type ${strategyType}`}>
      <ResultValue>
        {coverage.required_coverage_percent?.toFixed(1)}%
      </ResultValue>
      <ResultSubtext>
        {coverage.required_coverage_area_sq_m?.toFixed(2)} m² of{' '}
        {zoneArea.toFixed(2)} m² total zone area
        {clampedSubtext}
      </ResultSubtext>

      <FormulaDisplay>
        {strategyType === InspectionStrategy.A && (
          <>
            <FormulaLine>
              <strong>Formula:</strong> C<sub>R</sub> = max(2.5%, F
              <sub>COV</sub> × F<sub>CONS</sub> × F<sub>ZONE</sub> × C
              <sub>1</sub>)
            </FormulaLine>
            <FormulaLine>
              = max(2.5%, {coverage.f_cov?.toFixed(2)} ×{' '}
              {coverage.f_cons?.toFixed(2)} × {coverage.f_zone?.toFixed(2)} ×
              7.5%)
            </FormulaLine>
            <FormulaLine>
              = max(2.5%, <strong>{raw?.toFixed(2)}%</strong>)
            </FormulaLine>
            <FormulaLine>
              ={' '}
              <strong>{coverage.required_coverage_percent?.toFixed(2)}%</strong>
              {wasClamped && (
                <ClampedNote>
                  {' '}
                  ← clamped from {raw?.toFixed(2)}% to minimum 2.5%
                </ClampedNote>
              )}
            </FormulaLine>
          </>
        )}
        {strategyType === InspectionStrategy.B && (
          <>
            <FormulaLine>
              <strong>Formula:</strong> C<sub>R</sub> = F<sub>COV</sub> × F
              <sub>ACC</sub> × F<sub>LC</sub> × F<sub>CONS</sub> × F
              <sub>SH</sub> × F<sub>ZONE</sub> × C<sub>1</sub>
            </FormulaLine>
            <FormulaLine>
              = {coverage.f_cov?.toFixed(2)} × {coverage.f_acc?.toFixed(2)} ×{' '}
              {coverage.f_lc?.toFixed(2)} × {coverage.f_cons?.toFixed(2)} ×{' '}
              {coverage.f_sh?.toFixed(2)} × {coverage.f_zone?.toFixed(2)} × 25%
            </FormulaLine>
            <FormulaLine>
              = <strong>{raw?.toFixed(2)}%</strong>
              {wasClamped && typeBLimits && (
                <ClampedNote>
                  {' '}
                  ← clamped to{' '}
                  {raw! < typeBLimits.min
                    ? `floor ${typeBLimits.min}%`
                    : `ceiling ${typeBLimits.max}%`}
                </ClampedNote>
              )}
            </FormulaLine>
            {wasClamped && (
              <FormulaLine>
                ={' '}
                <strong>
                  {coverage.required_coverage_percent?.toFixed(2)}%
                </strong>
                <ClampedNote>
                  {' '}
                  (confidence range: {typeBLimits!.min}% – {typeBLimits!.max}%)
                </ClampedNote>
              </FormulaLine>
            )}
            {!wasClamped && (
              <FormulaLine>
                ={' '}
                <strong>
                  {coverage.required_coverage_percent?.toFixed(2)}%
                </strong>
              </FormulaLine>
            )}
          </>
        )}
      </FormulaDisplay>

      <ModifierTags>
        <Tag color={getModifierColor(coverage.f_cov)}>
          F<sub>COV</sub> = {coverage.f_cov?.toFixed(2)}×
        </Tag>
        {strategyType === InspectionStrategy.B && (
          <>
            <Tag color={getModifierColor(coverage.f_acc)}>
              F<sub>ACC</sub> = {coverage.f_acc?.toFixed(2)}×
            </Tag>
            <Tag color={getModifierColor(coverage.f_lc)}>
              F<sub>LC</sub> = {coverage.f_lc?.toFixed(2)}×
            </Tag>
            <Tag color={getModifierColor(coverage.f_sh)}>
              F<sub>SH</sub> = {coverage.f_sh?.toFixed(2)}×
            </Tag>
          </>
        )}
        <Tag color={getModifierColor(coverage.f_cons)}>
          F<sub>CONS</sub> = {coverage.f_cons?.toFixed(2)}×
        </Tag>
        <Tag color={getModifierColor(coverage.f_zone)}>
          F<sub>ZONE</sub> = {coverage.f_zone?.toFixed(2)}×
        </Tag>
      </ModifierTags>
    </ResultCard>
  )
}
